import React, { FC, memo } from 'react';

import { GProduct, ProductCardSlice } from '@/types';
import { useData } from '@/components';

import {
  ProductCardBtnBottomHover,
  ProductCardDefault,
  ProductCardLarge,
  ProductCardTraditional,
  ProductCardWithBorder,
  ProductCardWithBtnHover,
  ProductCardWithHighlighted,
  ProductCardWithInfoCentered,
  ProductCardWithSmallBtn,
} from './variations';
import { ProductProvider } from './context';

const Product: FC<IProductProps> = ({
  className,
  product,
  analyticsListName,
  slice,
  onKiosk,
  onKioskAddClicked,
}) => {
  const { productCard } = useData();
  const { activeProduct } = productCard;
  const isLargeCard = productCard?.displayProps?.isLargeCard;
  // this will be used only when the component is used from the
  // slice simulator, so we can preview on the CMS with the
  // mocked data how the card will look like
  let activeSlice = activeProduct as ProductCardSlice;

  if (slice) activeSlice = slice;

  const Component = ComponentsByVariations[activeSlice?.variation ?? 'default'];

  return (
    <ProductProvider product={product}>
      <Component
        analyticsListName={analyticsListName}
        className={className}
        isLargeCard={isLargeCard}
        onKiosk={onKiosk}
        onKioskAddClicked={onKioskAddClicked}
      />
    </ProductProvider>
  );
};

type ProductVariation = ProductCardSlice['variation'];

const ComponentsByVariations: Record<
  ProductVariation,
  React.FC<ProductVariationProps>
> = {
  default: ProductCardDefault,
  productCardWithSmallBtn: ProductCardWithSmallBtn,
  productCardWithBtnHover: ProductCardWithBtnHover,
  productCardWithHighlighted: ProductCardWithHighlighted,
  productCardWithBorder: ProductCardWithBorder,
  productCardWithInfoCenter: ProductCardWithInfoCentered,
  productCardLarge: ProductCardLarge,
  productCardTraditional: ProductCardTraditional,
  productCardBtnBottomHover: ProductCardBtnBottomHover,
};

export interface IProductProps {
  className?: string;
  product: GProduct;
  analyticsListName?: string;
  slice?: ProductCardSlice;
  onKiosk?: boolean;
  isAddToCartInKioskDisabled?: boolean;
  onKioskAddClicked?: () => void;
}

export interface IProductCard extends IProductProps {
  isLargeCard: boolean;
}

export type ProductVariationProps = Omit<IProductCard, 'product'>;

export default memo(Product);
